import { useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useSelector } from "react-redux";
import { exportExcel } from "@/excel";
import { useFilteredIds } from "@/hooks/useFilteredIds";
import { useTranslations } from "@/queries/useTranslations";
import type { IState } from "@/state";
import { buildExcelExportData, type ExcelExportColumn } from "@/translationFiltering";
import { LANGUAGES } from "@/types";
import { PreviewTable, PreviewWrapper } from "./ExcelPreview";

const EXPORT_COLUMNS: ExcelExportColumn[] = ["id", "context", ...LANGUAGES];

function ExportModal() {
    const { version } = useParams({ from: "/$version" });
    const visibleLanguages = useSearch({ from: "/$version", select: (state) => state.langs }) ?? LANGUAGES;
    const [columns, setColumns] = useState<ExcelExportColumn[]>(["id", "context", ...visibleLanguages]);
    const { data: translations } = useTranslations(version);
    const editsByContent = useSelector((state: IState) => state.edits[version]?.data);
    const { filtered } = useFilteredIds(version);
    const exportData =
        translations && filtered
            ? buildExcelExportData({ translations, editsByContent, filtered, columns })
            : { columns, rows: [] };

    const toggleColumn = (column: ExcelExportColumn) => {
        setColumns((current) =>
            current.includes(column)
                ? current.filter((selected) => selected !== column)
                : EXPORT_COLUMNS.filter((candidate) => candidate === column || current.includes(candidate)),
        );
    };

    const handleExport = () => {
        // Generate the Excel workbook as a Blob.
        const blob = exportExcel(exportData);
        // Create a URL for the blob.
        const url = URL.createObjectURL(blob);
        // Create a temporary anchor element to trigger the download.
        const a = document.createElement("a");
        a.href = url;
        const languages = LANGUAGES.filter((lang) => columns.includes(lang));
        a.download = languages.length > 0 ? `tm_content_${languages.join("_")}.xlsx` : "tm_content.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoke the URL once it's been used.
        URL.revokeObjectURL(url);
    };
    return (
        <div>
            <h2>Export all answer for {version} as xlsx</h2>
            <fieldset>
                <legend>Select columns to export</legend>
                {EXPORT_COLUMNS.map((column) => (
                    <label key={column}>
                        <input
                            type="checkbox"
                            checked={columns.includes(column)}
                            onChange={() => toggleColumn(column)}
                        />{" "}
                        {column}
                    </label>
                ))}
            </fieldset>
            {columns.length > 0 && (
                <>
                    <p>{exportData.rows.length + 1} Excel rows including the header row</p>
                    <PreviewWrapper>
                        <PreviewTable aria-label="Excel export preview">
                            <thead>
                                <tr>
                                    {columns.map((column) => (
                                        <th key={column}>{column}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {exportData.rows.slice(0, 10).map((row) => (
                                    <tr key={row.id}>
                                        {columns.map((column) => (
                                            <td key={column} title={row[column] ?? ""}>
                                                {row[column] ?? ""}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </PreviewTable>
                    </PreviewWrapper>
                </>
            )}
            <div>
                <button type="button" onClick={handleExport} disabled={columns.length === 0}>
                    Download (xlsx)
                </button>
            </div>
        </div>
    );
}

export default ExportModal;
